/**
 * The only source of "now" that offline expiry decisions may use.
 *
 * Everything this phase refuses is refused on a time comparison — an expired
 * snapshot, an expired credential, a code outside its window, a session past
 * twelve hours. All of them run on a computer whose clock nobody is correcting,
 * which somebody with the Windows account can set to anything they like.
 *
 * Reporting drift is not enough to make those decisions safe. `heartbeat.ts`
 * already measures `clockOffsetMs` — the gap between this machine and the server
 * at the last contact — and an obvious use of it would be "now = Date.now() +
 * offset". That does not work: winding the OS clock back moves `Date.now()`
 * itself, so the corrected value moves back with it and an expired snapshot
 * comes back to life.
 *
 * What works is a ratchet. At every instant the server has vouched for, the
 * device writes down a floor: the real time then, and the wall-clock reading it
 * saw at that moment. Afterwards, "now" is that floor plus however much wall
 * clock has elapsed since. During a five-day outage the floor advances by five
 * real days, which is correct. If the wall clock is moved backwards, the sum
 * comes out below the floor — and a value below the floor is impossible, so it
 * is clamped there and flagged.
 *
 * The flag is not a footnote. A clock that has gone backwards is either broken
 * or has been moved, and both deserve to be said out loud rather than absorbed.
 */
import { offlineDatabase, readDeviceState, updateDeviceState, type OfflineDatabase } from "../db/schema"

export interface EffectiveTime {
  /** Milliseconds since the epoch, never earlier than the last trusted instant. */
  nowMs: number
  /** True when the wall clock has moved backwards since that instant. */
  regressionDetected: boolean
  /** How long since the server last vouched for the time, in milliseconds. */
  sinceTrustedMs: number | null
}

/**
 * How far the floor is allowed to lag before it is rewritten. Without a
 * threshold every read would write, and this is read on every keystroke of a
 * code entry.
 */
const RATCHET_WRITE_INTERVAL_MS = 60_000

/**
 * Called after any successful, signature-verified exchange with the server —
 * a heartbeat, a snapshot fetch, a bootstrap step. `serverTimeMs` is the
 * server's own clock, which is the only clock here anybody has reason to trust.
 *
 * The wall-clock reading is taken at the same moment rather than recomputed
 * later, because the pair is what makes elapsed time measurable at all.
 */
export async function recordTrustedInstant(
  serverTimeMs: number,
  db: OfflineDatabase = offlineDatabase(),
  wallClockMs: number = Date.now()
): Promise<void> {
  await updateDeviceState(
    {
      timeFloorMs: serverTimeMs,
      timeFloorCapturedAtWallMs: wallClockMs,
      clockOffsetMs: wallClockMs - serverTimeMs,
      clockOffsetMeasuredAt: new Date(serverTimeMs).toISOString(),
      // Cleared here, and only here. A regression means the wall clock moved
      // backwards *since the last trusted instant* — once the server vouches for
      // the time again, the floor is re-anchored and the old observation says
      // nothing about the new one. Leaving it set would make a single NTP
      // correction, or an operator fixing a wrong date, permanently refuse every
      // offline sign-in on that computer with no way back but wiping its
      // storage.
      clockRegressionDetectedAt: null
    },
    db
  )
}

/**
 * The same thing, from the `serverTime` a sync response carries (§20.3).
 *
 * §20.3 asks for the offset to be measured "on every successful sync", and until
 * Phase 12 it was measured only on a heartbeat and a bootstrap. That was nearly
 * enough and wrong in one case that matters: an enrolled computer whose
 * heartbeat is blocked by a proxy but whose sync works has been reaching the
 * server all along, and was ratcheting from nothing.
 *
 * A string rather than a number because that is what is on the wire, and an
 * unparseable one is ignored rather than allowed to write `NaN` into the floor —
 * which would make every later comparison against it false, silently, forever.
 */
export async function recordTrustedInstantFrom(
  serverTime: string | null | undefined,
  db: OfflineDatabase = offlineDatabase(),
  wallClockMs: number = Date.now()
): Promise<void> {
  const parsed = serverTime === null || serverTime === undefined ? NaN : Date.parse(serverTime)
  if (Number.isNaN(parsed)) return

  await recordTrustedInstant(parsed, db, wallClockMs)
}

export async function effectiveNow(
  db: OfflineDatabase = offlineDatabase(),
  wallClockMs: number = Date.now()
): Promise<EffectiveTime> {
  const state = await readDeviceState(db)

  // A device that has never reached the server has no floor to stand on. Its
  // own clock is all there is, and readiness says so separately rather than
  // this function pretending to a confidence it does not have.
  if (state.timeFloorMs === null || state.timeFloorCapturedAtWallMs === null) {
    return { nowMs: wallClockMs, regressionDetected: false, sinceTrustedMs: null }
  }

  const elapsed = wallClockMs - state.timeFloorCapturedAtWallMs
  const candidate = state.timeFloorMs + elapsed

  if (candidate < state.timeFloorMs) {
    if (state.clockRegressionDetectedAt === null) {
      await updateDeviceState({ clockRegressionDetectedAt: new Date(state.timeFloorMs).toISOString() }, db)
    }

    return { nowMs: state.timeFloorMs, regressionDetected: true, sinceTrustedMs: 0 }
  }

  if (elapsed > RATCHET_WRITE_INTERVAL_MS) {
    await updateDeviceState({ timeFloorMs: candidate, timeFloorCapturedAtWallMs: wallClockMs }, db)
  }

  return {
    nowMs: candidate,
    regressionDetected: state.clockRegressionDetectedAt !== null,
    sinceTrustedMs: elapsed
  }
}

